import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useUsers, useCreateUser, useUpdateUser, useDisableUser, useEnableUser } from '../hooks/useUsers.js';
import { useClients } from '../hooks/useClients.js';
import { useAuthStore } from '../store/auth.store.js';
import Table from '../components/Table.jsx';
import Modal from '../components/Modal.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Badge from '../components/Badge.jsx';

const roleVariant   = { superadmin: 'info', admin: 'default' };
const statusVariant = { active: 'success', disabled: 'danger' };

// ── Calidad de contraseña ─────────────────────────────────────────────────────
function passwordStrength(pwd) {
  if (!pwd) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pwd.length >= 8)                        score++;
  if (pwd.length >= 12)                       score++;
  if (/[A-Z]/.test(pwd))                      score++;
  if (/[0-9]/.test(pwd))                      score++;
  if (/[^A-Za-z0-9]/.test(pwd))              score++;

  const levels = [
    { label: 'Muy débil', color: 'bg-red-500' },
    { label: 'Débil',     color: 'bg-orange-500' },
    { label: 'Regular',   color: 'bg-yellow-500' },
    { label: 'Buena',     color: 'bg-lime-500' },
    { label: 'Fuerte',    color: 'bg-emerald-500' },
    { label: 'Muy fuerte',color: 'bg-emerald-400' },
  ];
  return { score, ...levels[Math.min(score, levels.length - 1)] };
}

function PasswordStrengthBar({ password }) {
  const { score, label, color } = passwordStrength(password);
  if (!password) return null;
  const pct = Math.round((score / 5) * 100);
  return (
    <div className="mt-1.5 space-y-1">
      <div className="h-1.5 w-full rounded-full bg-slate-700 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-slate-400">{label}</p>
    </div>
  );
}

// ── Campo de contraseña con toggle ───────────────────────────────────────────
function PasswordInput({ label, value, onChange, showStrength = false }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <input
          className="input-base pr-10"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          autoComplete="new-password"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => setShow(s => !s)}
          className="absolute inset-y-0 right-0 px-3 flex items-center text-slate-400 hover:text-slate-200 transition-colors"
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
      {showStrength && <PasswordStrengthBar password={value} />}
    </div>
  );
}

// ── Modal de edición ──────────────────────────────────────────────────────────
function EditUserModal({ user, open, onClose, clients, currentUser }) {
  const [form, setForm] = useState({});
  const [newPwd, setNewPwd]       = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [err, setErr]   = useState(null);
  const updateMutation  = useUpdateUser(user?.id);

  // Sincronizar cuando se abre con un usuario distinto
  if (open && user && form.id !== user.id) {
    setForm({ id: user.id, name: user.name || '', email: user.email, role: user.role, clientId: user.client_id || '' });
    setNewPwd('');
    setConfirmPwd('');
    setErr(null);
  }

  if (!open) return null;

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSave = () => {
    setErr(null);
    if (newPwd && newPwd.length < 8) return setErr('La contraseña debe tener al menos 8 caracteres');
    if (newPwd && newPwd !== confirmPwd) return setErr('Las contraseñas no coinciden');

    const payload = {};
    if (form.name  !== user.name)         payload.name  = form.name;
    if (form.email !== user.email)        payload.email = form.email;
    if (currentUser?.role === 'superadmin') {
      if (form.role !== user.role)        payload.role     = form.role;
      const curClientId = user.client_id || '';
      if (form.clientId !== curClientId)  payload.clientId = form.clientId || null;
    }
    if (newPwd) payload.password = newPwd;

    if (!Object.keys(payload).length) return onClose();

    updateMutation.mutate(payload, {
      onSuccess: () => { onClose(); },
      onError: (e) => setErr(e.response?.data?.message || 'Error al guardar'),
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={`Editar usuario — ${user?.email}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button loading={updateMutation.isPending} onClick={handleSave}>Guardar</Button>
        </>
      }
    >
      <Input label="Nombre" value={form.name || ''} onChange={f('name')} />
      <Input label="Email" type="email" value={form.email || ''} onChange={f('email')} />

      {currentUser?.role === 'superadmin' && (
        <>
          <div>
            <label className="label">Rol</label>
            <select className="input-base" value={form.role || 'admin'}
              onChange={(e) => setForm(p => ({ ...p, role: e.target.value, clientId: '' }))}>
              <option value="admin">Admin</option>
              <option value="superadmin">Superadmin</option>
            </select>
          </div>
          {form.role === 'admin' && (
            <div>
              <label className="label">Cliente asignado</label>
              <select className="input-base" value={form.clientId || ''} onChange={f('clientId')}>
                <option value="">— Sin cliente (global) —</option>
                {(clients || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.business_name}</option>
                ))}
              </select>
            </div>
          )}
        </>
      )}

      <div className="border-t border-border pt-3 space-y-3">
        <p className="text-xs text-slate-500">Dejar vacío para no cambiar la contraseña</p>
        <PasswordInput label="Nueva contraseña" value={newPwd}
          onChange={(e) => setNewPwd(e.target.value)} showStrength />
        {newPwd && (
          <PasswordInput label="Confirmar contraseña" value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)} />
        )}
        {newPwd && confirmPwd && newPwd !== confirmPwd && (
          <p className="text-xs text-red-400">Las contraseñas no coinciden</p>
        )}
      </div>

      {err && <p className="text-sm text-red-400">{err}</p>}
    </Modal>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
const EMPTY_FORM = { name: '', email: '', password: '', confirm: '', role: 'admin', clientId: '' };

export default function Users() {
  const { data, isLoading } = useUsers();
  const createMutation = useCreateUser();
  const disableMutation = useDisableUser();
  const enableMutation  = useEnableUser();
  const currentUser = useAuthStore((s) => s.user);
  const { data: clients } = useClients();
  const [open, setOpen]         = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [err, setErr]   = useState(null);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleCreate = () => {
    setErr(null);
    if (form.password.length < 8)
      return setErr('La contraseña debe tener al menos 8 caracteres');
    if (form.password !== form.confirm)
      return setErr('Las contraseñas no coinciden');
    if (currentUser?.role === 'superadmin' && form.role === 'admin' && !form.clientId)
      return setErr('Seleccioná el cliente que tendrá asignado este usuario admin');

    const { confirm, ...payload } = form;
    if (!payload.clientId) delete payload.clientId;

    createMutation.mutate(payload, {
      onSuccess: () => { setOpen(false); setForm(EMPTY_FORM); },
      onError: (e) => setErr(e.response?.data?.message || 'Error al crear usuario'),
    });
  };

  const handleDisable = (user) => {
    if (user.id === currentUser?.id) return alert('No podés deshabilitar tu propia cuenta');
    if (!confirm(`¿Deshabilitar a ${user.name || user.email}?`)) return;
    disableMutation.mutate(user.id);
  };

  const handleEnable = (user) => {
    if (!confirm(`¿Re-habilitar a ${user.name || user.email}?`)) return;
    enableMutation.mutate(user.id);
  };

  const columns = [
    { key: 'name',        header: 'Nombre',  render: (r) => r.name || '—' },
    { key: 'email',       header: 'Email' },
    { key: 'role',        header: 'Rol',     render: (r) => <Badge variant={roleVariant[r.role]    || 'default'}>{r.role}</Badge> },
    { key: 'client_name', header: 'Empresa', render: (r) => r.client_name || <span className="text-slate-500">Global</span> },
    { key: 'status',      header: 'Estado',  render: (r) => <Badge variant={statusVariant[r.status] || 'default'}>{r.status}</Badge> },
    {
      key: 'actions', header: '',
      render: (r) => (
        <div className="flex gap-1">
          <Button variant="ghost" className="text-xs py-1 px-2"
            onClick={(e) => { e.stopPropagation(); setEditTarget(r); }}>
            Editar
          </Button>
          {r.status === 'active' && r.id !== currentUser?.id && (
            <Button variant="danger" className="text-xs py-1 px-2"
              onClick={(e) => { e.stopPropagation(); handleDisable(r); }}>
              Deshabilitar
            </Button>
          )}
          {r.status === 'disabled' && (
            <Button variant="secondary" className="text-xs py-1 px-2"
              onClick={(e) => { e.stopPropagation(); handleEnable(r); }}>
              Re-habilitar
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Usuarios</h2>
        <Button onClick={() => setOpen(true)}>+ Nuevo usuario</Button>
      </div>
      <Table columns={columns} data={data} loading={isLoading} empty="No hay usuarios" />

      <EditUserModal
        user={editTarget}
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        clients={clients}
        currentUser={currentUser}
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo usuario"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button loading={createMutation.isPending} onClick={handleCreate}>Crear</Button>
          </>
        }
      >
        <Input label="Nombre *"  value={form.name}  onChange={f('name')} />
        <Input label="Email *"   type="email" value={form.email} onChange={f('email')} />

        <PasswordInput label="Contraseña *" value={form.password} onChange={f('password')} showStrength />
        <PasswordInput label="Repetir contraseña *" value={form.confirm} onChange={f('confirm')} />

        {/* Coincidencia en tiempo real */}
        {form.confirm && form.password !== form.confirm && (
          <p className="text-xs text-red-400 -mt-1">Las contraseñas no coinciden</p>
        )}
        {form.confirm && form.password === form.confirm && form.confirm.length > 0 && (
          <p className="text-xs text-emerald-400 -mt-1">✓ Las contraseñas coinciden</p>
        )}

        <div>
          <label className="label">Rol</label>
          <select className="input-base" value={form.role} onChange={(e) => setForm(p => ({ ...p, role: e.target.value, clientId: '' }))}>
            <option value="admin">Admin</option>
            {currentUser?.role === 'superadmin' && <option value="superadmin">Superadmin</option>}
          </select>
        </div>

        {/* Selector de cliente: solo superadmin creando un admin */}
        {currentUser?.role === 'superadmin' && form.role === 'admin' && (
          <div>
            <label className="label">Cliente asignado *</label>
            <select className="input-base" value={form.clientId} onChange={f('clientId')}>
              <option value="">— Seleccioná un cliente —</option>
              {(clients || []).map((c) => (
                <option key={c.id} value={c.id}>{c.code} — {c.business_name}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">El usuario solo verá datos de este cliente</p>
          </div>
        )}

        {err && <p className="text-sm text-red-400">{err}</p>}
      </Modal>
    </div>
  );
}
